import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';

export interface PromoCode {
  id: string;
  code: string;
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
  minSpend: number;
  maxDiscountCap?: number;
  maxUsageCount: number;
  currentUsageCount: number;
  applicableUniversities: string[]; // ['ALL'] or university IDs
  startDate: string;
  endDate: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  createdBy: string;
}

export interface StudentRewardProfile {
  studentId: string;
  studentName: string;
  phoneNumber: string;
  universityName: string;
  referralCode: string;
  totalInvited: number;
  activeBookings: number;
  pointsBalance: number;
  freeTripsBalance: number;
  earnedDiscountMonths: string[];
  level: 'BRONZE' | 'SILVER' | 'GOLD' | 'VIP';
  lastUpdated: string;
}

export interface PromoRewardStats {
  totalPromoCodes: number;
  activePromoCodes: number;
  totalUsages: number;
  totalDiscountSaved: number;
  activeAmbassadorsCount: number;
  totalPointsGranted: number;
}

const LOCAL_PROMO_KEY = 'oto_promo_codes_v1';
const LOCAL_REWARDS_KEY = 'oto_student_rewards_v1';

export const promoRewardService = {
  /**
   * Fetch all promo codes (Firestore + local storage fallback)
   */
  async getAllPromoCodes(): Promise<PromoCode[]> {
    try {
      const colRef = collection(db, 'promoCodes');
      const q = query(colRef, orderBy('createdAt', 'desc'), limit(100));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const list: PromoCode[] = snap.docs.map((d) => ({
          ...(d.data() as PromoCode),
          id: d.id,
        }));
        if (typeof window !== 'undefined') {
          localStorage.setItem(LOCAL_PROMO_KEY, JSON.stringify(list));
        }
        return list;
      }
    } catch (err) {
      console.warn('Firestore promoCodes fetch failed, using local storage:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_PROMO_KEY);
        if (raw) return JSON.parse(raw);
      } catch (_) {}
    }

    return [];
  },

  /**
   * Create a new promo code
   */
  async createPromoCode(
    input: Omit<PromoCode, 'id' | 'createdAt' | 'currentUsageCount'>
  ): Promise<PromoCode> {
    const id = `promo_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newPromo: PromoCode = {
      ...input,
      id,
      code: input.code.trim().toUpperCase(),
      currentUsageCount: 0,
      createdAt: new Date().toISOString(),
    };

    try {
      await setDoc(doc(db, 'promoCodes', id), newPromo);
    } catch (err) {
      console.warn('Firestore setDoc failed for promo code:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_PROMO_KEY);
        const existing: PromoCode[] = raw ? JSON.parse(raw) : [];
        const updated = [newPromo, ...existing];
        localStorage.setItem(LOCAL_PROMO_KEY, JSON.stringify(updated));
      } catch (_) {}
    }

    return newPromo;
  },

  /**
   * Toggle promo code active status
   */
  async togglePromoCodeStatus(id: string, isActive: boolean): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'promoCodes', id), {
        isActive,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('Firestore update failed for promo code status:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_PROMO_KEY);
        if (raw) {
          const list: PromoCode[] = JSON.parse(raw);
          const updated = list.map((p) => (p.id === id ? { ...p, isActive } : p));
          localStorage.setItem(LOCAL_PROMO_KEY, JSON.stringify(updated));
        }
      } catch (_) {}
    }

    return true;
  },

  /**
   * Delete promo code
   */
  async deletePromoCode(id: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'promoCodes', id));
    } catch (err) {
      console.warn('Firestore delete failed for promo code:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_PROMO_KEY);
        if (raw) {
          const list: PromoCode[] = JSON.parse(raw);
          const filtered = list.filter((p) => p.id !== id);
          localStorage.setItem(LOCAL_PROMO_KEY, JSON.stringify(filtered));
        }
      } catch (_) {}
    }

    return true;
  },

  /**
   * Get all students with reward/referral stats
   */
  async getAllStudentRewards(): Promise<StudentRewardProfile[]> {
    const list: StudentRewardProfile[] = [];
    try {
      const snap = await getDocs(collection(db, 'students'));

      snap.forEach((d) => {
        const data = d.data() as any;
        const totalInvited = Number(data.referralStats?.totalInvited) || 0;
        const activeBookings = Number(data.referralStats?.activeBookedCount) || 0;
        const pointsBalance = Number(data.pointsBalance) || activeBookings * 50;
        const freeTripsBalance = Number(data.freeTripsBalance) || Math.floor(activeBookings / 10);

        let level: 'BRONZE' | 'SILVER' | 'GOLD' | 'VIP' = 'BRONZE';
        if (activeBookings >= 30) level = 'VIP';
        else if (activeBookings >= 20) level = 'GOLD';
        else if (activeBookings >= 10) level = 'SILVER';

        list.push({
          studentId: d.id,
          studentName: data.fullName || 'طالب OTO',
          phoneNumber: data.phoneNumber || '—',
          universityName: data.universityName || 'الجامعة',
          referralCode: data.referralCode || `OTO-${d.id.substring(0, 5).toUpperCase()}`,
          totalInvited,
          activeBookings,
          pointsBalance,
          freeTripsBalance,
          earnedDiscountMonths: data.referralStats?.earnedDiscountMonths || [],
          level,
          lastUpdated: data.updatedAt || new Date().toISOString(),
        });
      });

      if (list.length > 0) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(LOCAL_REWARDS_KEY, JSON.stringify(list));
        }
        return list;
      }
    } catch (err) {
      console.warn('Firestore fetch student rewards failed, checking cache:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_REWARDS_KEY);
        if (raw) return JSON.parse(raw);
      } catch (_) {}
    }

    return [];
  },

  /**
   * Grant bonus points or free trips to a student
   */
  async grantStudentBonus(
    studentId: string,
    bonus: { points?: number; freeTrips?: number; reason: string }
  ): Promise<boolean> {
    try {
      const studentRef = doc(db, 'students', studentId);
      const snap = await getDoc(studentRef);
      const current = snap.exists() ? snap.data() : {};
      const newPoints = (Number(current?.pointsBalance) || 0) + (bonus.points || 0);
      const newTrips = (Number(current?.freeTripsBalance) || 0) + (bonus.freeTrips || 0);

      await setDoc(
        studentRef,
        {
          pointsBalance: newPoints,
          freeTripsBalance: newTrips,
          lastBonusGrantedAt: new Date().toISOString(),
          lastBonusReason: bonus.reason,
        },
        { merge: true }
      );
      return true;
    } catch (err) {
      console.error('Failed to grant bonus:', err);
      return false;
    }
  },

  /**
   * Calculate stats for the dashboard showcase
   */
  calculateStats(promos: PromoCode[], rewards: StudentRewardProfile[]): PromoRewardStats {
    const totalPromoCodes = promos.length;
    const activePromoCodes = promos.filter((p) => p.isActive).length;
    let totalUsages = 0;
    let totalDiscountSaved = 0;

    for (const p of promos) {
      totalUsages += p.currentUsageCount;
      if (p.discountType === 'FIXED') {
        totalDiscountSaved += p.currentUsageCount * p.discountValue;
      } else {
        totalDiscountSaved += p.currentUsageCount * (p.discountValue * 2); // Approximate average
      }
    }

    const activeAmbassadorsCount = rewards.filter((r) => r.activeBookings > 0).length;
    const totalPointsGranted = rewards.reduce((sum, r) => sum + r.pointsBalance, 0);

    return {
      totalPromoCodes,
      activePromoCodes,
      totalUsages,
      totalDiscountSaved,
      activeAmbassadorsCount,
      totalPointsGranted,
    };
  },
};
